// To call back (Call Logs v2, CU-869fmbgfg): one row per caller number, read from
// GET calls/queue/ (contract ~/pm/contracts/V2-1.md v1). Pure (no React / MUI) so
// it is testable without the components barrel.
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { formatUkPhone } from 'src/utils/ukPhone';
import { callLineLabel, serverLineLabel } from 'src/utils/callLineLabel';
import { normalizeUnifiedCall } from '../callLog.normalize';
import { APP_TIME_FORMAT, CALL_BACK_WINDOW_DAYS, CALL_LOGS_ACCENT } from '../callLog.constants';

dayjs.extend(utc);
dayjs.extend(timezone);

const UK = 'Europe/London';

// To call back is "everyone owed a call now" (CU-869fmcbha): the server reads the last 7 days
// ending now, whatever date range the page shows, so the numbers never change with it.
// The tiles are filters (`?tile=`). The three owed buckets are exclusive and always add up to
// To call back; Being chased is a sub-filter of that list; To qualify and Closed sit apart.
export const QUEUE_TILE = {
  TO_CALL_BACK: 'to_call_back',
  OVERDUE: 'overdue',
  DUE_TODAY: 'due_today',
  DUE_NEXT_OPENING: 'due_next_opening',
  TO_QUALIFY: 'to_qualify',
  BEING_CHASED: 'being_chased',
  COULDNT_REACH: 'couldnt_reach',
  EXPIRED: 'expired',
  ARCHIVED: 'archived',
  // The previous release's tiles (still sent by the server, not shown).
  NEW_CALLERS: 'new_callers',
  OVERNIGHT: 'overnight',
};
// Expired, in plain words (tile help and the empty list).
export const EXPIRED_TEXT = `Missed more than ${CALL_BACK_WINDOW_DAYS} days ago and never called back, including calls from before your line was connected. Restore one to put it back in To call back.`;
export const QUEUE_TILES = [
  { key: QUEUE_TILE.OVERDUE, label: 'Overdue', hint: "Owed a call-back and past the practice's call-back target. The target is counted in opening hours; the time on each row is how long ago it was due." },
  { key: QUEUE_TILE.DUE_TODAY, label: 'Due today', hint: 'Owed a call-back before the end of today and not overdue yet.' },
  { key: QUEUE_TILE.DUE_NEXT_OPENING, label: 'Next opening', hint: 'Due when the practice next opens.' },
  { key: QUEUE_TILE.BEING_CHASED, label: 'Being chased', hint: 'Called back at least once and not reached yet (try 1–2 of 3).' },
  { key: QUEUE_TILE.TO_QUALIFY, label: 'New numbers', hint: 'New numbers (people, not calls) not linked to a patient and not owed a call: not in the call-back list.' },
  // "Not reached" fits the card on one line at 1280px; the Status chip still says "Couldn't reach".
  { key: QUEUE_TILE.COULDNT_REACH, label: 'Not reached', hint: "Couldn't reach: tried 3 times and still not reached." },
  { key: QUEUE_TILE.EXPIRED, label: 'Expired', hint: EXPIRED_TEXT },
  { key: QUEUE_TILE.ARCHIVED, label: 'Archived', hint: 'Callers put away as not a patient, spam, dealt with elsewhere or a duplicate. Restore brings them back.' },
];
export const tileLabel = (key) => QUEUE_TILES.find((t) => t.key === key)?.label || 'To call back';
// Owed now, one bucket each: these three add up to To call back.
export const OWED_BUCKETS = [QUEUE_TILE.OVERDUE, QUEUE_TILE.DUE_TODAY, QUEUE_TILE.DUE_NEXT_OPENING];

// The cards, in three groups with the Team tab's card row (KPICardsRow). No card is active by
// default; pressing a card filters, pressing it again clears. One accent for all of them
// (CU-869fnzh3x): urgency shows in the rows' Waiting chips, not in rainbow card colours.
const CARD_SUB = {
  [QUEUE_TILE.OVERDUE]: 'past the target',
  [QUEUE_TILE.DUE_TODAY]: 'before close',
  [QUEUE_TILE.DUE_NEXT_OPENING]: 'at opening',
  // People (numbers), not calls: All calls counts calls from these numbers.
  // People (numbers) in the list's own window; All calls counts calls.
  [QUEUE_TILE.TO_QUALIFY]: `people · ${CALL_BACK_WINDOW_DAYS}d`,
  [QUEUE_TILE.COULDNT_REACH]: 'tried 3 times',
  [QUEUE_TILE.EXPIRED]: `over ${CALL_BACK_WINDOW_DAYS} days`,
  [QUEUE_TILE.ARCHIVED]: 'can restore',
};
const card = (counts, key) => {
  const tile = QUEUE_TILES.find((t) => t.key === key);
  return {
    key,
    label: tile.label,
    value: typeof counts[key] === 'number' ? counts[key] : '—',
    accent: CALL_LOGS_ACCENT,
    subText: CARD_SUB[key],
    tooltipText: tile.hint,
    hideDelta: true,
  };
};
export function queueCardGroups(counts = {}) {
  return [
    { key: 'owed', label: 'Owed a call now', cards: OWED_BUCKETS.map((k) => card(counts, k)) },
    { key: 'qualify', label: 'To qualify', cards: [card(counts, QUEUE_TILE.TO_QUALIFY)] },
    { key: 'closed', label: 'Closed', cards: [QUEUE_TILE.COULDNT_REACH, QUEUE_TILE.EXPIRED, QUEUE_TILE.ARCHIVED].map((k) => card(counts, k)) },
  ];
}
// Every card, flat (tests and lookups).
export const queueCards = (counts = {}) => queueCardGroups(counts).flatMap((g) => g.cards);

export const EMPTY_QUEUE_COUNTS = Object.freeze(Object.fromEntries(Object.values(QUEUE_TILE).map((k) => [k, null])));

const str = (v) => (typeof v === 'string' && v.trim() ? v.trim() : '');
const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

export function readCounts(raw) {
  const counts = raw && typeof raw === 'object' ? raw : {};
  return Object.fromEntries(Object.values(QUEUE_TILE).map((k) => [k, typeof counts[k] === 'number' ? counts[k] : null]));
}

// The queue row's representative call, shaped like a Call Logs row, so the existing
// dialogs (Log outcome, Create lead, Link to existing, Not a patient, ...) work on it
// unchanged. It carries the queue row's next_actions and number.
function callRowOf(raw, line) {
  const last = raw.last_contact && typeof raw.last_contact === 'object' ? raw.last_contact : {};
  const id = str(raw.representative_call_id) || str(last.call_id);
  if (!id) return null;
  const owed = raw.owed === true;
  return {
    ...normalizeUnifiedCall({
      id,
      channel: id.startsWith('whatsapp:') ? 'whatsapp' : 'phone',
      direction: 'inbound',
      from_number: raw.number,
      outcome: owed ? 'missed' : (str(last.outcome) || 'unknown'),
      started_at: (owed && raw.owed_since) || last.at || null,
      lead: raw.lead || null,
      site_label: line.site,
      site_number: line.siteNumber,
      hunt_group: line.ringGroup,
      follow_up_state: owed ? 'unresponded' : '',
      follow_up_overdue: raw.overdue === true,
      caller_label: raw.caller_label,
      caller_hints: raw.caller_hints,
      caller_candidates: raw.caller_candidates,
      next_actions: raw.next_actions,
    }),
    // The queue's own number wins over anything the synthetic row derived.
    contact: str(raw.number) || 'Unknown',
  };
}

// Archived tile rows carry `archive` (contract UX-A §1); every other row has null.
export function readArchive(raw) {
  if (!raw || typeof raw !== 'object' || raw.id == null) return null;
  const kind = raw.kind === 'archive' ? 'archive' : 'flag';
  return {
    kind,
    id: raw.id,
    reason: str(raw.reason),
    reasonLabel: str(raw.reason_label) || str(raw.reason).replace(/_/g, ' '),
    note: str(raw.note),
    archivedAt: raw.archived_at || null,
    archivedByName: str(raw.archived_by_name),
  };
}

const ukDisplay = (n) => { const uk = formatUkPhone(n); return uk && uk !== n ? uk : ''; };

export function normalizeQueueRow(raw) {
  if (!raw || typeof raw !== 'object' || !str(raw.number)) return null;
  const lineRaw = raw.line && typeof raw.line === 'object' ? raw.line : {};
  const line = {
    site: str(lineRaw.site),
    ringGroup: str(lineRaw.ring_group),
    label: str(lineRaw.label) || [str(lineRaw.site), str(lineRaw.ring_group)].filter(Boolean).join(' · '),
    siteNumber: str(lineRaw.site_number),
    // "<line name> · <number>", the same as the All calls Line column (CU-869fn55ct); '' from an older backend.
    lineLabel: str(lineRaw.line_label),
  };
  const last = raw.last_contact && typeof raw.last_contact === 'object' ? raw.last_contact : {};
  const lead = raw.lead && typeof raw.lead === 'object' && raw.lead.id != null ? raw.lead : null;
  const leadName = lead ? [lead.first_name, lead.last_name].filter(Boolean).join(' ').trim() || `Lead ${lead.id}` : '';
  return {
    id: str(raw.id) || str(raw.number),
    number: str(raw.number),
    // UK format from the number itself (0113 496 0199) when it is a UK number; otherwise
    // the server's display (a non-UK number comes back from formatUkPhone unchanged).
    numberDisplay: ukDisplay(str(raw.number)) || str(raw.number_display) || str(raw.number),
    callerLabel: str(raw.caller_label),
    lead: lead ? { id: lead.id, name: leadName, isArchived: lead.is_archived === true } : null,
    line,
    inCount: num(raw.in_count),
    // Missed calls still owed a call-back; the server lists at most OWED_IDS_LIMIT (20).
    missedCount: Array.isArray(raw.owed_call_ids) ? raw.owed_call_ids.length : 0,
    outCount: num(raw.out_count),
    lastContactAt: last.at || null,
    lastDirection: str(last.direction),
    state: str(raw.state),
    owed: raw.owed === true,
    owedSince: raw.owed_since || null,
    dueAt: raw.due_at || null,
    overdue: raw.overdue === true,
    overdueMinutes: num(raw.overdue_minutes),
    attempts: num(raw.attempts),
    maxAttempts: num(raw.max_attempts) || 3,
    overnight: raw.overnight === true,
    expired: raw.expired === true || str(raw.state) === QUEUE_TILE.EXPIRED,
    tiles: Array.isArray(raw.tiles) ? raw.tiles : [],
    claim: readClaim(raw.claim),
    hint: str(raw.caller_hints?.flag?.label),
    archive: readArchive(raw.archive),
    callRow: callRowOf(raw, line),
  };
}

export function readClaim(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const by = raw.by && typeof raw.by === 'object' ? raw.by : {};
  return {
    id: raw.id,
    state: str(raw.state) || 'claimed',
    byName: str(by.name),
    isYou: by.is_you === true,
    expiresAt: raw.expires_at || null,
    callId: str(raw.call_id),
  };
}

// "2h 10m", "40 min", "3d 2h".
export function minutesText(minutes) {
  const m = Math.max(0, Math.round(minutes || 0));
  if (m < 60) return `${m} min`;
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  const rest = m % 60;
  if (d) return h ? `${d}d ${h}h` : `${d}d`;
  return rest ? `${h}h ${rest}m` : `${h}h`;
}

const isNewCaller = (row) => row.callerLabel === 'new_caller' || row.tiles.includes(QUEUE_TILE.NEW_CALLERS);

// Graded urgency (UX-A §7): overdue red, due today amber, new caller blue, the rest neutral.
export const URGENCY = { OVERDUE: 'overdue', DUE_TODAY: 'due_today', NEW: 'new', NEUTRAL: 'neutral' };
export function urgencyOf(row, now = Date.now()) {
  if (!row || !row.owed) return URGENCY.NEUTRAL;
  if (row.overdue) return URGENCY.OVERDUE;
  const dueToday = row.tiles.includes(QUEUE_TILE.DUE_TODAY)
    || (row.dueAt && dayjs(row.dueAt).isValid() && dayjs(row.dueAt).tz(UK).isSame(dayjs(now).tz(UK), 'day'));
  if (dueToday) return URGENCY.DUE_TODAY;
  if (isNewCaller(row)) return URGENCY.NEW;
  return URGENCY.NEUTRAL;
}
export const URGENCY_TONE = { [URGENCY.OVERDUE]: 'error', [URGENCY.DUE_TODAY]: 'warning', [URGENCY.NEW]: 'info', [URGENCY.NEUTRAL]: 'grey' };

// Waiting column: how long they have waited or when the call is due, graded by how late
// (CU-869fnzh3x: three tints, no solid fills, so the worst rows stand out without a wall of
// red). Overdue under OVERDUE_MID_MINUTES is amber, under OVERDUE_STRONG_MINUTES light red,
// older than that a stronger red; due within the hour (or now) amber, due later grey.
// When it becomes due is counted in opening hours (the target); overdue minutes are plain
// clock time since then (server queue.py rows_for), as the hint on the chip says.
export const OVERDUE_MID_MINUTES = 4 * 60;
export const OVERDUE_STRONG_MINUTES = 2 * 24 * 60;
export const DUE_SOON_MINUTES = 60;
export const OVERDUE_GRADE = { SOFT: 'soft', MID: 'mid', STRONG: 'strong' };
export function overdueGrade(minutes) {
  if (minutes >= OVERDUE_STRONG_MINUTES) return OVERDUE_GRADE.STRONG;
  if (minutes >= OVERDUE_MID_MINUTES) return OVERDUE_GRADE.MID;
  return OVERDUE_GRADE.SOFT;
}
const dueHint = (row) => (row.dueAt && dayjs(row.dueAt).isValid()
  ? `Due ${dayjs(row.dueAt).tz(UK).format(`ddd DD MMM, ${APP_TIME_FORMAT}`)} (target counted in opening hours)`
  : '');
export function dueChip(row, now = Date.now()) {
  if (!row) return null;
  if (!row.owed) return { text: 'Not owed a call', tone: 'grey' };
  if (row.expired) return { text: 'Expired', tone: 'grey' };
  if (row.overdue) {
    const grade = overdueGrade(row.overdueMinutes);
    return {
      text: `Overdue ${minutesText(row.overdueMinutes)}`,
      tone: grade === OVERDUE_GRADE.SOFT ? 'warning' : 'error',
      grade,
      strong: grade === OVERDUE_GRADE.STRONG,
      hint: dueHint(row),
    };
  }
  if (row.dueAt && dayjs(row.dueAt).isValid()) {
    const mins = dayjs(row.dueAt).diff(dayjs(now), 'minute');
    if (mins <= 0) return { text: 'Due now', tone: 'warning', hint: dueHint(row) };
    return { text: `Due in ${minutesText(mins)}`, tone: mins <= DUE_SOON_MINUTES ? 'warning' : 'grey', hint: dueHint(row) };
  }
  if (row.overnight) return { text: 'Overnight', tone: URGENCY_TONE[urgencyOf(row, now)] };
  return { text: 'Waiting', tone: URGENCY_TONE[urgencyOf(row, now)] };
}

// Overnight help text (Status chip).
export const OVERNIGHT_HINT = 'Missed while the practice was closed';
// Due (or overdue) now: no longer "Overnight", whatever the time it was missed.
const dueNow = (row, now) => row.overdue || Boolean(row.dueAt && dayjs(row.dueAt).isValid() && !dayjs(row.dueAt).isAfter(dayjs(now)));

// "Status" column: where the call-back effort stands, not how urgent it is. Who the caller is
// ("Not a lead yet", "Patient") is under their name in the Caller column, never repeated here.
export function statusChip(row, now = Date.now()) {
  if (!row) return null;
  if (row.state === 'couldnt_reach') return { text: "Couldn't reach", tone: 'grey' };
  if (row.expired) return { text: 'Expired', tone: 'grey' };
  if (row.attempts >= 1) return { text: `Try ${row.attempts} of ${row.maxAttempts}`, tone: 'grey' };
  if (row.overnight && !dueNow(row, now)) return { text: 'Overnight', tone: 'grey', hint: OVERNIGHT_HINT };
  return { text: row.owed ? 'Waiting' : '', tone: 'grey' };
}

// The server lists at most this many owed call ids (queue.py OWED_IDS_LIMIT).
export const OWED_IDS_LIMIT = 20;

// "3 missed · 1 call-back": the missed calls still owed, then our calls back. A row owed
// nothing (e.g. Archived) counts its incoming calls instead. Nothing at all reads
// "No calls yet".
export function countsText(row) {
  const parts = [];
  if (row.missedCount > 0) parts.push(`${row.missedCount >= OWED_IDS_LIMIT ? `${OWED_IDS_LIMIT}+` : row.missedCount} missed`);
  else if (row.inCount > 0) parts.push(`${row.inCount} incoming`);
  if (row.outCount > 0) parts.push(`${row.outCount} call-back${row.outCount === 1 ? '' : 's'}`);
  return parts.length ? parts.join(' · ') : 'No calls yet';
}

// The last contact, short enough to show in full (CU-869fnzh3x): "Today 19:33", or
// "Thu 29 Oct 12:51" (UK time); '' when unknown. The cell's tooltip says it is the last contact.
export function lastContactText(row, now = Date.now()) {
  if (!row.lastContactAt || !dayjs(row.lastContactAt).isValid()) return '';
  const at = dayjs(row.lastContactAt).tz(UK);
  const today = dayjs(now).tz(UK).isSame(at, 'day');
  return at.format(today ? '[Today] HH:mm' : 'ddd DD MMM HH:mm');
}

// "Sam is calling" / "You're calling"; null when nobody is (no chip at all).
export function claimChip(claim) {
  if (!claim) return null;
  if (claim.isYou) return { text: "You're calling", tone: 'info' };
  const first = claim.byName.split(' ')[0] || 'Someone';
  return { text: `${first} is calling`, tone: 'info' };
}

// Line column: the server's line label ("main · 01908 731705 · Existing", as All calls shows it),
// else (an older backend) the one call line label (utils/callLineLabel): "Reading · Existing",
// never an internal connection name, else the number that was rung in UK format.
// Expired column: when the owed calls started ("Owed since 12 Oct").
export function owedSinceText(row) {
  if (!row?.owedSince || !dayjs(row.owedSince).isValid()) return '';
  return `Owed since ${dayjs(row.owedSince).tz(UK).format('D MMM')}`;
}

// Any number inside the label in UK format (01970 17557), like the rest of the app.
const ukNumbers = (text) => (typeof text === 'string' ? text.replace(/\+?\d[\d ]{8,}\d/g, (m) => formatUkPhone(m) || m) : text);
export const lineText = (row) => ukNumbers(serverLineLabel(row?.line?.lineLabel, row?.line?.ringGroup) || callLineLabel(row?.line || {}));

// Who they are, for the first line: the patient, else the readable number.
export const rowTitle = (row) => row.lead?.name || row.numberDisplay;

// Second line: caller type · number (the line has its own column).
export function rowSubtitle(row, labelText) {
  const parts = [];
  if (labelText) parts.push(labelText);
  if (row.lead?.name) parts.push(row.numberDisplay);
  return parts.join(' · ');
}

// The lead tables' paging (10 rows, then 25 / 50), so the list is the same height as theirs.
export const QUEUE_PAGE_SIZE = 10;
export const QUEUE_PAGE_SIZE_OPTIONS = [10, 25, 50];
// Query for GET calls/queue/. No dates: the list is everyone owed a call now, whatever range
// the page shows (the server ignores any it is sent).
export function queueParams({ tile = QUEUE_TILE.TO_CALL_BACK, query = '', page = 0, pageSize = QUEUE_PAGE_SIZE } = {}) {
  const params = new URLSearchParams();
  params.set('tile', tile || QUEUE_TILE.TO_CALL_BACK);
  if (query && query.trim()) params.set('q', query.trim().slice(0, 100));
  params.set('page', String(page + 1));
  params.set('page_size', String(pageSize));
  return params;
}
